import { useState, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { NumericFormat } from "react-number-format";
import debounce from "lodash/debounce";
import PieChart from "./PieChart";
import AlertDialog from "./AlertDialog";

export default function ProposalForm({
  opportunityId,
  setProposals,
  setOpportunities,
  setIsCreatingProposal,
  setIsEditingProposal,
  proposal = null,
}) {
  const [proposalFormData, setProposalFormData] = useState(
    proposal
      ? {
          title: proposal.title,
          drawingDate: proposal.drawingDate
            ? new Date(proposal.drawingDate).toISOString().split("T")[0]
            : "",
          sequenceOfWork: proposal.sequenceOfWork,
          anticipatedStartDate: proposal.anticipatedStartDate
            ? new Date(proposal.anticipatedStartDate)
                .toISOString()
                .split("T")[0]
            : "",
          workingDays: proposal.workingDays,
          depositTerms: proposal.depositTerms,
          paymentTerms: proposal.paymentTerms,
          totalCost: parseFloat(proposal.totalCost).toFixed(2),
          totalPrice: parseFloat(proposal.totalPrice).toFixed(2),
          lineItems: proposal.lineItems.map((item) => ({
            ...item,
            cost: parseFloat(item.cost).toFixed(2),
            price: parseFloat(item.price).toFixed(2),
            markup: parseFloat(item.markup).toFixed(2),
          })),
          attachments: proposal.attachments,
        }
      : {
          title: "",
          drawingDate: "",
          sequenceOfWork: "",
          anticipatedStartDate: "",
          workingDays: 0,
          depositTerms: "",
          paymentTerms: "",
          totalCost: "0.00",
          totalPrice: "0.00",
          lineItems: [
            {
              title: "",
              price: "0.00",
              cost: "0.00",
              markup: "0.00",
              scopeOfWork: "",
              conditionOfWork: "",
            },
          ],
          attachments: [],
        },
  );
  const [newProposalAttachments, setNewProposalAttachments] = useState([]);
  const [excelData, setExcelData] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });

  const handleProposalInputChange = (e) => {
    const { name, value } = e.target;
    setProposalFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    setProposalFormData((prev) => {
      const lineItems = [...prev.lineItems];
      const currentItem = { ...lineItems[index] };

      if (field === "cost") {
        const parsedCost = parseFloat(value) || 0;
        currentItem.cost = parsedCost.toFixed(2);
        // Recalculate price from existing markup
        currentItem.price = (
          parsedCost *
          (1 + parseFloat(currentItem.markup) / 100)
        ).toFixed(2);
      } else if (field === "price" || field === "markup") {
        // Just store the raw value while typing — don't recalculate anything
        currentItem[field] = value;
      } else {
        currentItem[field] = value;
      }

      lineItems[index] = currentItem;

      const totalCost = lineItems
        .reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0)
        .toFixed(2);
      const totalPrice = lineItems
        .reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
        .toFixed(2);

      return { ...prev, lineItems, totalCost, totalPrice };
    });
  };

  const handlePriceBlur = (index) => {
    setProposalFormData((prev) => {
      const lineItems = [...prev.lineItems];
      const currentItem = { ...lineItems[index] };

      const price = parseFloat(currentItem.price) || 0;
      const cost = parseFloat(currentItem.cost) || 0;

      currentItem.price = price.toFixed(2);
      currentItem.markup =
        cost > 0 ? ((price / cost - 1) * 100).toFixed(2) : "0.00";

      lineItems[index] = currentItem;

      const totalPrice = lineItems
        .reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
        .toFixed(2);

      return { ...prev, lineItems, totalPrice };
    });
  };

  const handleMarkupBlur = (index) => {
    setProposalFormData((prev) => {
      const lineItems = [...prev.lineItems];
      const currentItem = { ...lineItems[index] };

      const markup = parseFloat(currentItem.markup) || 0;
      const cost = parseFloat(currentItem.cost) || 0;

      currentItem.markup = markup.toFixed(2);
      currentItem.price = (cost * (1 + markup / 100)).toFixed(2);

      lineItems[index] = currentItem;

      const totalPrice = lineItems
        .reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
        .toFixed(2);

      return { ...prev, lineItems, totalPrice };
    });
  };

  const addLineItem = () => {
    setProposalFormData((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          title: "",
          price: "0.00",
          cost: "0.00",
          markup: "0.00",
          scopeOfWork: "",
          conditionOfWork: "",
        },
      ],
    }));
  };

  const removeLineItem = (index) => {
    setProposalFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
      totalCost: prev.lineItems
        .filter((_, i) => i !== index)
        .reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0)
        .toFixed(2),
      totalPrice: prev.lineItems
        .filter((_, i) => i !== index)
        .reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
        .toFixed(2),
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map((file) => ({
      name: file.name,
      file,
    }));
    setNewProposalAttachments((prev) => [...prev, ...newFiles]);
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array", raw: false });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          blankrows: false,
        });

        console.log("Parsed Excel Data:", JSON.stringify(jsonData, null, 2));

        const validRows = jsonData.filter(
          (row) =>
            Array.isArray(row) && row[0] !== undefined && row[0] !== null,
        );

        console.log("Valid Rows Length:", validRows.length);
        console.log("Valid Rows First 20:", validRows.slice(0, 20));

        const selectedSectionsRow = validRows.find(
          (row) =>
            typeof row[0] === "string" &&
            row[0].toLowerCase().startsWith("selected sections:"),
        );
        if (!selectedSectionsRow) {
          throw new Error("Selected Sections row not found");
        }
        const sections = selectedSectionsRow[0]
          .replace(/Selected Sections:/i, "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        console.log("Sections:", sections);

        const cleanNumber = (value) => {
          if (typeof value === "string") {
            const cleaned = value.replace(/[, ]/g, "");
            const parsed = parseFloat(cleaned);
            return isNaN(parsed) ? 0 : parsed;
          }
          return typeof value === "number" && !isNaN(value) ? value : 0;
        };

        const sectionTotals = sections.map((section) => {
          const sectionTotalRow = validRows.find(
            (row) =>
              typeof row[0] === "string" &&
              row[0].includes(`${section} Total:`),
          );
          const cost = sectionTotalRow
            ? cleanNumber(sectionTotalRow[1]).toFixed(2)
            : "0.00";
          console.log(
            `Section: ${section}, Total Row:`,
            sectionTotalRow,
            "Cost:",
            cost,
          );
          return {
            title: section,
            cost: parseFloat(cost),
          };
        });

        const grandTotalRow = validRows.find(
          (row) =>
            typeof row[0] === "string" &&
            (row[0].toLowerCase().startsWith("grand total:") ||
              row[0]
                .toLowerCase()
                .startsWith("grand without additional markups total:")),
        );
        const grandTotalValue = grandTotalRow
          ? cleanNumber(grandTotalRow[5] || grandTotalRow[1]).toFixed(2)
          : sectionTotals
              .reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0)
              .toFixed(2);
        const grandTotal = parseFloat(grandTotalValue);
        console.log(
          "Grand Total Row:",
          grandTotalRow,
          "Grand Total Value:",
          grandTotalValue,
          "Parsed Grand Total:",
          grandTotal,
        );

        const plansDateRow = validRows[7]; // Directly access row 8 (index 7)
        let plansDate = "";
        if (
          plansDateRow &&
          plansDateRow[3] &&
          typeof plansDateRow[3] === "string"
        ) {
          const rawDate = plansDateRow[3].trim();
          const parsedDate = new Date(rawDate);
          if (!isNaN(parsedDate.getTime())) {
            plansDate = parsedDate.toISOString().split("T")[0];
          } else {
            const dateMatch = rawDate.match(/(\d{4}-\d{2}-\d{2})/);
            plansDate = dateMatch ? dateMatch[1] : "";
          }
        }
        console.log("Plans Date Row:", plansDateRow, "Plans Date:", plansDate);

        const notesRow = validRows.find(
          (row) => typeof row[0] === "string" && row[0].includes("***"),
        );
        if (!notesRow || typeof notesRow[0] !== "string") {
          throw new Error("Notes row (A18) not found or invalid");
        }
        const notesText = notesRow[0];
        console.log("Notes Row Index:", validRows.indexOf(notesRow));
        console.log("Notes Text (A18):", notesText);

        const noteSections = notesText
          .split("***")
          .map((s) => s.trim())
          .filter(Boolean);

        const cleanText = (text) => {
          if (typeof text !== "string") return "";
          return text
            .replace(/_x000D_/g, "")
            .replace(/\r/g, "")
            .replace(/\n+/g, "\n")
            .replace(/[ \t]+/g, " ")
            .replace(/\n /g, "\n")
            .trim();
        };

        const sectionNotes = sections.map((section) => {
          const costCode = section.slice(0, 8);
          const sowSection = noteSections.find((note) =>
            note.startsWith(`${costCode} SOW:`),
          );
          const cowSection = noteSections.find((note) =>
            note.startsWith(`${costCode} COW:`),
          );

          const sowLines = sowSection
            ? cleanText(sowSection.replace(`${costCode} SOW:`, "").trim())
            : "";
          const cowLines = cowSection
            ? cleanText(cowSection.replace(`${costCode} COW:`, "").trim())
            : "";

          console.log(
            `Section: ${section}, Cost Code: ${costCode}, SOW:`,
            sowLines,
            "COW:",
            cowLines,
          );
          return {
            title: section,
            scopeOfWork: sowLines,
            conditionOfWork: cowLines,
          };
        });

        const newLineItems = sectionTotals.map((total) => {
          const notes = sectionNotes.find((note) => note.title === total.title);
          return {
            title: total.title,
            price: total.cost.toFixed(2),
            cost: total.cost.toFixed(2),
            markup: "0.00",
            scopeOfWork: notes?.scopeOfWork || "",
            conditionOfWork: notes?.conditionOfWork || "",
          };
        });

        setProposalFormData((prev) => ({
          ...prev,
          lineItems: newLineItems,
          totalCost: grandTotal.toFixed(2),
          totalPrice: grandTotal.toFixed(2),
          drawingDate: plansDate || prev.drawingDate,
        }));

        setExcelData({
          sectionTotals: sectionTotals.map((item) => ({
            ...item,
            cost: item.cost.toFixed(2),
          })),
          grandTotal: grandTotal.toFixed(2),
        });
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: `Failed to parse Excel file: ${error.message}`,
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const formDataToSend = new FormData();
      formDataToSend.append("title", proposalFormData.title);
      formDataToSend.append("opportunity", opportunityId);
      formDataToSend.append("drawingDate", proposalFormData.drawingDate);
      formDataToSend.append("sequenceOfWork", proposalFormData.sequenceOfWork);
      formDataToSend.append(
        "anticipatedStartDate",
        proposalFormData.anticipatedStartDate,
      );
      formDataToSend.append("workingDays", proposalFormData.workingDays);
      formDataToSend.append("depositTerms", proposalFormData.depositTerms);
      formDataToSend.append("paymentTerms", proposalFormData.paymentTerms);
      formDataToSend.append("totalCost", proposalFormData.totalCost);
      formDataToSend.append("totalPrice", proposalFormData.totalPrice);
      formDataToSend.append(
        "lineItems",
        JSON.stringify(proposalFormData.lineItems),
      );
      newProposalAttachments.forEach((att) => {
        formDataToSend.append("attachments", att.file);
      });

      const url = proposal
        ? `https://erp-production-72da01c8e651.herokuapp.com/api/proposals/${proposal._id}`
        : "https://erp-production-72da01c8e651.herokuapp.com/api/proposals";

      const response = await axios({
        method: proposal ? "put" : "post",
        url,
        data: formDataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setProposals((prev) =>
        proposal
          ? prev.map((prop) =>
              prop._id === proposal._id ? response.data : prop,
            )
          : [...prev, response.data],
      );
      setOpportunities((prev) =>
        prev.map((opp) =>
          opp._id === opportunityId
            ? {
                ...opp,
                relatedProposals: [...opp.relatedProposals, response.data],
              }
            : opp,
        ),
      );
      setIsCreatingProposal(false);
      setIsEditingProposal(null);
      setProposalFormData({
        title: "",
        drawingDate: "",
        sequenceOfWork: "",
        anticipatedStartDate: "",
        workingDays: 0,
        depositTerms: "",
        paymentTerms: "",
        totalCost: "0.00",
        totalPrice: "0.00",
        lineItems: [
          {
            title: "",
            price: "0.00",
            cost: "0.00",
            markup: "0.00",
            scopeOfWork: "",
            conditionOfWork: "",
          },
        ],
        attachments: [],
      });
      setNewProposalAttachments([]);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: proposal
          ? "Proposal updated successfully!"
          : "Proposal created successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to save proposal: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-6">
      <h2 className="text-lg font-semibold text-orange-400 mb-4">
        {proposal ? "Edit Proposal" : "Create Proposal"}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Title
            </label>
            <input
              type="text"
              name="title"
              value={proposalFormData.title}
              onChange={handleProposalInputChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Drawing Date
            </label>
            <input
              type="date"
              name="drawingDate"
              value={proposalFormData.drawingDate}
              onChange={handleProposalInputChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-400">
              Sequence of Work
            </label>
            <textarea
              name="sequenceOfWork"
              value={proposalFormData.sequenceOfWork}
              onChange={handleProposalInputChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Anticipated Start Date
            </label>
            <input
              type="date"
              name="anticipatedStartDate"
              value={proposalFormData.anticipatedStartDate}
              onChange={handleProposalInputChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Working Days
            </label>
            <input
              type="number"
              name="workingDays"
              value={proposalFormData.workingDays}
              onChange={handleProposalInputChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-400">
              Deposit Percentage
            </label>
            <input
              name="depositTerms"
              value={proposalFormData.depositTerms}
              onChange={handleProposalInputChange}
              className="mt-1 block w-full max-w-32 rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-400">
              Payment Terms
            </label>
            <textarea
              name="paymentTerms"
              value={proposalFormData.paymentTerms}
              onChange={handleProposalInputChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="totalCost"
                  className="block text-sm font-medium text-gray-400 mb-1"
                >
                  Total Cost
                </label>
                <NumericFormat
                  id="totalCost"
                  value={proposalFormData.totalCost}
                  onValueChange={(values) => {
                    const { floatValue } = values;
                    handleProposalInputChange({
                      target: {
                        name: "totalCost",
                        value: floatValue ? floatValue.toFixed(2) : "0.00",
                      },
                    });
                  }}
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={true}
                  thousandSeparator=","
                  prefix="$"
                  className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm py-2 px-3"
                  required
                  readOnly
                />
              </div>
              <div>
                <label
                  htmlFor="totalPrice"
                  className="block text-sm font-medium text-gray-400 mb-1"
                >
                  Total Price
                </label>
                <NumericFormat
                  id="totalPrice"
                  value={proposalFormData.totalPrice}
                  onValueChange={(values) => {
                    const { floatValue } = values;
                    handleProposalInputChange({
                      target: {
                        name: "totalPrice",
                        value: floatValue ? floatValue.toFixed(2) : "0.00",
                      },
                    });
                  }}
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={true}
                  thousandSeparator=","
                  prefix="$"
                  className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm py-2 px-3"
                  required
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-400">
              Upload Excel File
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelUpload}
              className="mt-2 block w-full text-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-400 file:text-white hover:file:bg-orange-500"
            />
          </div>
          {excelData && excelData.sectionTotals && (
            <div className="col-span-2">
              <h4 className="text-sm font-medium text-gray-400 mb-2">
                Cost Breakdown
              </h4>
              <PieChart
                sectionTotals={excelData.sectionTotals}
                grandTotal={excelData.grandTotal}
              />
            </div>
          )}
          <div className="col-span-2">
            <h4 className="text-sm font-medium text-gray-400 mb-2">
              Line Items
            </h4>
            {proposalFormData.lineItems.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-1 gap-4 md:grid-cols-3 mb-2 bg-gray-700 p-4 rounded-md"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Title
                  </label>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) =>
                      handleLineItemChange(index, "title", e.target.value)
                    }
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Price
                  </label>
                  <NumericFormat
                    value={item.price}
                    onChange={(e) =>
                      handleLineItemChange(
                        index,
                        "price",
                        e.target.value.replace(/[^0-9.-]/g, ""),
                      )
                    }
                    onBlur={() => handlePriceBlur(index)}
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={true}
                    thousandSeparator=","
                    prefix="$"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    required
                  />
                  <div className="text-xs text-gray-300 mt-1">
                    Actual Markup:{" "}
                    {parseFloat(item.cost) > 0
                      ? (
                          (parseFloat(item.price) / parseFloat(item.cost) - 1) *
                          100
                        ).toFixed(2)
                      : "0.00"}
                    %
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Cost
                  </label>
                  <NumericFormat
                    value={item.cost}
                    onValueChange={(values) => {
                      const { floatValue } = values;
                      handleLineItemChange(index, "cost", floatValue || 0);
                    }}
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={true}
                    thousandSeparator=","
                    prefix="$"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Markup (%)
                  </label>
                  <NumericFormat
                    value={item.markup}
                    onChange={(e) =>
                      handleLineItemChange(
                        index,
                        "markup",
                        e.target.value.replace(/[^0-9.-]/g, ""),
                      )
                    }
                    onBlur={() => handleMarkupBlur(index)}
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={true}
                    suffix="%"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-400">
                    Scope of Work
                  </label>
                  <textarea
                    value={item.scopeOfWork}
                    onChange={(e) =>
                      handleLineItemChange(index, "scopeOfWork", e.target.value)
                    }
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-400">
                    Condition of Work
                  </label>
                  <textarea
                    value={item.conditionOfWork}
                    onChange={(e) =>
                      handleLineItemChange(
                        index,
                        "conditionOfWork",
                        e.target.value,
                      )
                    }
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                {proposalFormData.lineItems.length > 1 && (
                  <div>
                    <button
                      type="button"
                      onClick={() => removeLineItem(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addLineItem}
              className="text-orange-400 hover:text-orange-300 mt-2"
            >
              Add Line Item
            </button>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-400">
              Attachments
            </label>
            {proposalFormData.attachments.map((att, idx) => (
              <div key={idx} className="text-sm text-gray-300">
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:underline"
                >
                  {att.name}
                </a>
              </div>
            ))}
            {newProposalAttachments.map((att, idx) => (
              <div key={idx} className="text-sm text-gray-300">
                {att.name}
              </div>
            ))}
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              className="mt-2 block w-full text-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-400 file:text-white hover:file:bg-orange-500"
              accept=".pdf,.jpg,.png,.jpeg"
            />
          </div>
        </div>
        <div className="flex justify-end space-x-4 mt-4">
          <button
            type="button"
            onClick={() => {
              setIsCreatingProposal(false);
              setIsEditingProposal(null);
              setProposalFormData({
                title: "",
                drawingDate: "",
                sequenceOfWork: "",
                anticipatedStartDate: "",
                workingDays: 0,
                depositTerms: "",
                paymentTerms: "",
                totalCost: "0.00",
                totalPrice: "0.00",
                lineItems: [
                  {
                    title: "",
                    price: "0.00",
                    cost: "0.00",
                    markup: "0.00",
                    scopeOfWork: "",
                    conditionOfWork: "",
                  },
                ],
                attachments: [],
              });
              setNewProposalAttachments([]);
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500"
          >
            {proposal ? "Update Proposal" : "Create Proposal"}
          </button>
        </div>
      </form>
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={alert.onConfirm}
        showCancel={alert.showCancel}
      />
    </div>
  );
}
